// Wall-comic hover — idle-scope kind, with a special second mode when a
// stack-table is engaged: the current top sleeve of the active stack
// becomes the wall-comic hover target. Picking up a stack-table sleeve
// flows through the same `inspectWallComic` path as a regular wall comic
// (the stack-table kind itself doesn't have an interactE — this kind
// handles E for both modes).

import { registerInteractable } from "../_interactable-registry.js";
import { addOutline, removeOutline } from "../helpers.js";

registerInteractable({
	id: "wall-comic",
	precedence: 50,
	// ASK-137 §84 — THIS KIND'S OWN PROMPT AND SUBJECT. `stage.js` used to enumerate every kind by
	// hand, twice (`_updateEHint`'s hover ladder and `_hoveredProduct`), which is exactly what the
	// registry's own header says a registry exists to end. The prompt a shopper reads and the product
	// it names belong where the rest of this kind's behaviour already is.
	hoverHint: '<kbd>E</kbd> Inspect',
	productOf(stage, active) {
		return active?.userData?.interactable?.product ?? null;
	},

	// Note: NOT suppressed by activeStackTable — the stack-table-active
	// branch substitutes the top sleeve as hover target (handled in
	// evaluateActive). Otherwise standard idle-scope gating applies.
	suppressedBy(stage) {
		if (stage.boxState === "open" || stage.boxState === "browsing" || stage.boxState === "hero") return true;
		if (stage.activeFixtureHero) return true;
		// stack-table active is NOT a suppression — see evaluateActive below.
		if (stage.activeVinylBin)    return true;
		if (stage.hoveredPileComic)  return true;
		if (stage.hoveredBook)       return true;
		if (stage.spinnerRack?.hoveredComic) return true;
		if (!stage.wallComics?.length && !stage.activeStackTable) return true;
		return false;
	},

	// Two modes:
	//   1. stack-table active → top sleeve of that stack is the hover target
	//   2. otherwise → raycast against wall comic meshes
	evaluateActive(stage) {
		if (stage.activeStackTable) {
			const stack = stage.activeStackTable;
			const N = stack.sleeves.length;
			const topSleeveIdx = (N - 1 - stack.cycleCount + N * N) % N;
			return stack.sleeves[topSleeveIdx]?.mesh ?? null;
		}
		if (!stage.wallComics?.length) return null;
		// 🔴 ASK-129 F-108c — RECURSIVE, THEN RESOLVED BACK TO THE REGISTERED ITEM.
		//
		// This was non-recursive, which is correct only while every seated product is a single mesh.
		// A catalogue product is a multi-mesh clone, and registering one of its boards makes the
		// other faces dead: MEASURED on the plane with the crosshair dead centre on a floor comic
		// (target NDC 0,0), the app's own ray hit "Comic_3" carrying the ZONE seat — the right ITEM —
		// while the registered mesh was a different child, so the hover found nothing and E did
		// nothing. Hitting any part of the item is what a customer means by aiming at it, so the ray
		// goes deep and the hit is walked back UP to the object that carries `wallComicIdx` — which
		// is the entry, and for a multi-mesh product that is its root, so the hero lifts the whole
		// thing. A single-mesh comic resolves to itself on the first step: unchanged, by construction.
		const hits = stage.raycaster.intersectObjects(
			stage.wallComics.map((wc) => wc.mesh), true,
		);
		for (const h of hits) {
			for (let n = h.object; n; n = n.parent) {
				if (n.userData?.wallComicIdx != null) return n;
			}
		}
		return null;
	},

	commitHover(stage, prev, next) {
		if (prev === next) return;
		if (prev) removeOutline(prev);
		stage.hoveredWallComic = next;
		if (next) addOutline(next);
	},

	interactE(stage, mesh) {
		if (!mesh) return false;
		// A closed-but-still-active long box clings to lidNode + activeBoxId
		// state until something either re-opens it or explicitly clears it.
		// A wall-comic inspection is "explicitly clears it" — same as the
		// legacy E handler.
		if (stage.boxState === "closed") stage.forceCloseActiveBox();
		// Stack-table sleeves cycle between slots, so the wallComics entry's
		// captured `worldPos` is stale by the time the player presses E —
		// refresh it to the sleeve's current physical position so the
		// put-back animation returns to the correct slot.
		const def = mesh.userData?.interactable;
		if (def?.fixtureKind === "stack-table") {
			const entry = stage.wallComics.find((wc) => wc.mesh === mesh);
			if (entry) entry.worldPos = mesh.position.clone();
		}
		stage.inspectWallComic(mesh.userData.wallComicIdx);
		return true;
	},
});
